import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiSearch, FiEye, FiEdit, FiTrash2, FiSend, FiDollarSign, FiFileText, FiCheck, FiX, FiArrowRight, FiClock, FiChevronDown, FiChevronRight, FiInbox, FiPhone, FiMail, FiAlertOctagon } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import QuoteRequestNotes from '../components/QuoteRequestNotes';

const INVOICES_PER_PAGE = 50;

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Quote-only users (e.g. crew chiefs granted quote access without payroll/admin)
  // see only the Quotes and Quote Requests tabs — invoices stay hidden.
  const canManagePayroll = !!user?.can_manage_payroll;
  const quotesOnly = !canManagePayroll;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') || (quotesOnly ? 'quotes' : 'invoices')
  );

  // Invoice state — search/filter initialized from the URL so it survives
  // navigating into an invoice and back.
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(true);
  const [invoiceSearch, setInvoiceSearch] = useState(searchParams.get('inv_q') || '');
  const [debouncedInvoiceSearch, setDebouncedInvoiceSearch] = useState(searchParams.get('inv_q') || '');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState(searchParams.get('inv_status') || '');
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [hasMoreInvoices, setHasMoreInvoices] = useState(true);

  // Quote state
  const [quotes, setQuotes] = useState([]);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [quoteSearch, setQuoteSearch] = useState(searchParams.get('q_q') || '');
  const [quoteStatusFilter, setQuoteStatusFilter] = useState(searchParams.get('q_status') || '');
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // Quote requests state
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [quoteRequestsLoading, setQuoteRequestsLoading] = useState(false);
  const [quoteRequestStatusFilter, setQuoteRequestStatusFilter] = useState('new');
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  // Unbilled time state
  const [unbilledJobs, setUnbilledJobs] = useState([]);
  const [unbilledTotal, setUnbilledTotal] = useState(0);
  const [unbilledLoading, setUnbilledLoading] = useState(true);
  const [showUnbilled, setShowUnbilled] = useState(false);
  const [showAllUnbilled, setShowAllUnbilled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debounce invoice search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedInvoiceSearch(invoiceSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [invoiceSearch]);

  // Quote-only users can't view invoices — bounce them to the quotes tab if a
  // ?tab=invoices URL lands them there.
  useEffect(() => {
    if (quotesOnly && activeTab === 'invoices') {
      setActiveTab('quotes');
    }
  }, [quotesOnly, activeTab]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      if (quotesOnly) return;
      loadInvoices(true);
    } else if (activeTab === 'quotes') {
      loadQuotes();
    } else if (activeTab === 'requests') {
      loadQuoteRequests();
    }
  }, [activeTab, invoiceStatusFilter, quoteStatusFilter, quoteRequestStatusFilter, debouncedInvoiceSearch]);

  // Keep the URL in sync with tab + search/filter state, so it survives
  // navigating into an invoice/quote and coming back. replace:true avoids
  // pushing a new history entry on every keystroke.
  useEffect(() => {
    const params = {};
    if (activeTab) params.tab = activeTab;
    if (invoiceSearch) params.inv_q = invoiceSearch;
    if (invoiceStatusFilter) params.inv_status = invoiceStatusFilter;
    if (quoteSearch) params.q_q = quoteSearch;
    if (quoteStatusFilter) params.q_status = quoteStatusFilter;
    setSearchParams(params, { replace: true });
  }, [activeTab, invoiceSearch, invoiceStatusFilter, quoteSearch, quoteStatusFilter]);

  const loadQuoteRequests = async () => {
    try {
      setQuoteRequestsLoading(true);
      const config = await getAuthConfig();
      const params = new URLSearchParams();
      if (quoteRequestStatusFilter && quoteRequestStatusFilter !== 'all') {
        params.append('status', quoteRequestStatusFilter);
      }
      const res = await axios.get(`${API_URL}/quote-requests?${params.toString()}`, config);
      setQuoteRequests(res.data || []);
    } catch (err) {
      console.error('Failed to load quote requests:', err);
    } finally {
      setQuoteRequestsLoading(false);
    }
  };

  const handleConvertRequest = async (req) => {
    // Navigate to the new-quote form with prefill data in the URL
    const params = new URLSearchParams();
    if (req.full_name) params.append('bill_to_name', req.full_name);
    if (req.email) params.append('bill_to_email', req.email);
    if (req.phone) params.append('bill_to_phone', req.phone);
    if (req.property_address) params.append('parcel_address', req.property_address);
    const notesParts = [];
    if (req.survey_type) notesParts.push(`Survey type: ${req.survey_type}`);
    if (req.project_details) notesParts.push(`Project details:\n${req.project_details}`);
    if (notesParts.length) params.append('notes', notesParts.join('\n\n'));
    params.append('from_request', String(req.id));
    navigate(`/billing/quotes/new?${params.toString()}`);
  };

  const handleMarkRequestStatus = async (id, status) => {
    try {
      const config = await getAuthConfig();
      await axios.patch(`${API_URL}/quote-requests/${id}`, { status }, config);
      loadQuoteRequests();
      if (selectedRequestId === id && status === 'spam') setSelectedRequestId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Delete this quote request? This cannot be undone.')) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/quote-requests/${id}`, config);
      if (selectedRequestId === id) setSelectedRequestId(null);
      loadQuoteRequests();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const [importingEmail, setImportingEmail] = useState(false);
  const handleImportFromEmail = async () => {
    setImportingEmail(true);
    try {
      const config = await getAuthConfig();
      const res = await axios.post(`${API_URL}/quote-requests/import-from-email`, { notify_days: 1 }, config);
      const r = res.data;
      alert(`Imported ${r.imported} new, skipped ${r.skipped} (already imported), ${r.errors} errors.`);
      loadQuoteRequests();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to import from email');
    }
    setImportingEmail(false);
  };

  const handleTabChange = (tab) => {
    // Just update the tab — the URL-sync effect writes it (and preserves the
    // search/filter params) so switching tabs doesn't wipe your search.
    setActiveTab(tab);
  };

  const loadInvoices = useCallback(async (reset = false, loadMore = false) => {
    try {
      if (!loadMore) setInvoiceLoading(true);
      const config = await getAuthConfig();
      const offset = reset ? 0 : invoices.length;

      const params = new URLSearchParams();
      if (invoiceStatusFilter === 'Imported-unpaid') {
        params.append('status', 'Imported');
        params.append('unpaid', '1');
      } else if (invoiceStatusFilter) {
        params.append('status', invoiceStatusFilter);
      }
      if (debouncedInvoiceSearch.trim()) params.append('search', debouncedInvoiceSearch.trim());
      params.append('limit', INVOICES_PER_PAGE);
      params.append('offset', offset);

      const url = `${API_URL}/invoices?${params.toString()}`;
      const response = await axios.get(url, config);
      const invoicesData = response.data.invoices || [];
      const total = response.data.total || 0;

      if (reset) {
        setInvoices(invoicesData);
      } else {
        setInvoices(prev => [...prev, ...invoicesData]);
      }

      setTotalInvoices(total);
      setHasMoreInvoices(offset + invoicesData.length < total);
      setInvoiceLoading(false);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
      setInvoiceLoading(false);
    }
  }, [invoiceStatusFilter, debouncedInvoiceSearch, invoices.length]);

  const loadQuotes = async () => {
    try {
      const config = await getAuthConfig();
      let url = `${API_URL}/quotes`;
      if (quoteStatusFilter) {
        url += `?status=${quoteStatusFilter}`;
      }
      const response = await axios.get(url, config);
      setQuotes(response.data || []);
      setQuoteLoading(false);
    } catch (error) {
      console.error('Error loading quotes:', error);
      setQuoteLoading(false);
    }
  };

  const loadUnbilledTime = useCallback(async () => {
    try {
      setUnbilledLoading(true);
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/unbilled-time-summary`, config);
      setUnbilledJobs(response.data.jobs || []);
      setUnbilledTotal(response.data.total_count || 0);
    } catch (error) {
      // Silently fail - user may not have permission
      setUnbilledJobs([]);
      setUnbilledTotal(0);
    } finally {
      setUnbilledLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUnbilledTime();
  }, [loadUnbilledTime]);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      const config = await getAuthConfig();
      const endpoint = deleteType === 'invoice' ? 'invoices' : 'quotes';
      await axios.delete(`${API_URL}/${endpoint}/${itemToDelete.id}`, config);
      setShowDeleteModal(false);
      setItemToDelete(null);
      if (deleteType === 'invoice') {
        loadInvoices(true);
      } else {
        loadQuotes();
      }
    } catch (error) {
      console.error(`Error deleting ${deleteType}:`, error);
      const errorMsg = error.response?.data?.error || `Failed to delete ${deleteType}`;
      alert(errorMsg);
    }
  };

  const handleSendInvoice = async (invoice) => {
    if (!window.confirm(`Mark invoice ${invoice.invoice_number} as sent?`)) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/invoices/${invoice.id}/send`, {}, config);
      loadInvoices();
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Failed to send invoice');
    }
  };

  const handleSendQuote = async (quote) => {
    if (!window.confirm(`Mark quote ${quote.quote_number} as sent?`)) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/quotes/${quote.id}/send`, {}, config);
      loadQuotes();
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Failed to send quote');
    }
  };

  const handleApproveQuote = async (quote) => {
    if (!window.confirm(`Mark quote ${quote.quote_number} as approved?`)) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/quotes/${quote.id}/approve`, {}, config);
      loadQuotes();
    } catch (error) {
      console.error('Error approving quote:', error);
      alert('Failed to approve quote');
    }
  };

  const handleDeclineQuote = async (quote) => {
    if (!window.confirm(`Mark quote ${quote.quote_number} as declined?`)) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/quotes/${quote.id}/decline`, {}, config);
      loadQuotes();
    } catch (error) {
      console.error('Error declining quote:', error);
      alert('Failed to decline quote');
    }
  };

  const handleConvertQuote = async (quote) => {
    if (!window.confirm(`Convert quote ${quote.quote_number} to an invoice?`)) return;
    try {
      const config = await getAuthConfig();
      // This shortcut has no way to ask about change orders. Rather than silently bill the
      // quote and leave an approved CO stranded, hand off to the quote page, which owns that
      // choice. Non-fatal if the check itself fails — fall through and convert as before.
      try {
        const co = await axios.get(`${API_URL}/quotes/${quote.id}/billable-change-orders`, config);
        const pending = co.data?.change_orders || [];
        if (pending.length) {
          alert(
            `Quote ${quote.quote_number} has ${pending.length} approved change order(s) worth `
            + `$${(co.data.total || 0).toFixed(2)} that nothing has billed yet.\n\n`
            + `Opening the quote so you can choose whether to put them on this invoice.`
          );
          navigate(`/billing/quotes/${quote.id}`);
          return;
        }
      } catch (probe) {
        console.error('Could not check change orders before converting:', probe);
      }
      const response = await axios.post(`${API_URL}/quotes/${quote.id}/convert`, {}, config);
      alert(`Quote converted! Invoice ${response.data.invoice.invoice_number} created.`);
      loadQuotes();
      // Switch to invoices tab to see the new invoice
      handleTabChange('invoices');
      loadInvoices();
    } catch (error) {
      console.error('Error converting quote:', error);
      alert(error.response?.data?.error || 'Failed to convert quote');
    }
  };

  const getStatusStyle = (status) => {
    const styles = {
      'Draft': { bg: '#e5e7eb', color: '#374151' },
      'Sent': { bg: '#dbeafe', color: '#1d4ed8' },
      'Paid': { bg: '#dcfce7', color: '#166534' },
      'Overdue': { bg: '#fee2e2', color: '#dc2626' },
      'Approved': { bg: '#dcfce7', color: '#166534' },
      'Declined': { bg: '#fee2e2', color: '#dc2626' },
      'Expired': { bg: '#fef3c7', color: '#b45309' },
      'Converted': { bg: '#ede9fe', color: '#7c3aed' },
      'Cancelled': { bg: '#f3f4f6', color: '#6b7280' },
      'Imported': { bg: '#f3f4f6', color: '#6b7280' }
    };
    return styles[status] || styles['Draft'];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString();
  };

  // Server now handles filtering, use invoices directly
  const filteredInvoices = invoices;

  // Filter quotes
  const filteredQuotes = quotes.filter(q => {
    const searchLower = quoteSearch.toLowerCase();
    return (
      (q.quote_number || '').toLowerCase().includes(searchLower) ||
      (q.client_name || '').toLowerCase().includes(searchLower) ||
      (q.bill_to_name || '').toLowerCase().includes(searchLower) ||
      (q.bill_to_company || '').toLowerCase().includes(searchLower) ||
      (q.jobs && q.jobs.some(job => 
        (job.job_number || '').toLowerCase().includes(searchLower) ||
        (job.property_address || '').toLowerCase().includes(searchLower)
      ))
    );
  });

  // Calculate stats
  const invoiceStats = {
    outstanding: invoices.filter(i => ['Sent', 'Overdue'].includes(i.status)).reduce((sum, i) => sum + (i.balance_due || 0), 0),
    draft: invoices.filter(i => i.status === 'Draft').length,
    sent: invoices.filter(i => i.status === 'Sent').length,
    overdue: invoices.filter(i => i.status === 'Overdue').length,
    paid: invoices.filter(i => i.status === 'Paid').length
  };

  const quoteStats = {
    total: quotes.reduce((sum, q) => sum + (q.total || 0), 0),
    draft: quotes.filter(q => q.status === 'Draft').length,
    sent: quotes.filter(q => q.status === 'Sent').length,
    approved: quotes.filter(q => q.status === 'Approved').length,
    declined: quotes.filter(q => q.status === 'Declined').length
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#1f2937' }}>Billing</h1>
        <p style={{ color: '#6b7280', marginTop: '8px' }}>
          Manage invoices and quotes
        </p>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0',
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        {!quotesOnly && (
        <button
          onClick={() => handleTabChange('invoices')}
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: activeTab === 'invoices' ? '#2563eb' : '#6b7280',
            borderBottom: activeTab === 'invoices' ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: '-2px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiDollarSign /> Invoices
        </button>
        )}
        <button
          onClick={() => handleTabChange('quotes')}
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: activeTab === 'quotes' ? '#16a34a' : '#6b7280',
            borderBottom: activeTab === 'quotes' ? '2px solid #16a34a' : '2px solid transparent',
            marginBottom: '-2px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiFileText /> Quotes
        </button>
        <button
          onClick={() => handleTabChange('requests')}
          style={{
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: activeTab === 'requests' ? '#ea580c' : '#6b7280',
            borderBottom: activeTab === 'requests' ? '2px solid #ea580c' : '2px solid transparent',
            marginBottom: '-2px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FiInbox /> Quote Requests
          {quoteRequests.filter(r => r.status === 'new').length > 0 && activeTab !== 'requests' && (
            <span style={{
              backgroundColor: '#ea580c', color: 'white', borderRadius: '10px',
              fontSize: '11px', padding: '2px 7px', fontWeight: '700',
            }}>
              {quoteRequests.filter(r => r.status === 'new').length}
            </span>
          )}
        </button>
      </div>

      {/* Invoices Tab Content */}
      {activeTab === 'invoices' && (
        <>
          {/* Invoice Stats */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Outstanding</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#2563eb' }}>{formatCurrency(invoiceStats.outstanding)}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Draft</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#374151' }}>{invoiceStats.draft}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Sent</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1d4ed8' }}>{invoiceStats.sent}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Overdue</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{invoiceStats.overdue}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Paid</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#166534' }}>{invoiceStats.paid}</div>
            </div>
          </div>

          {/* Unbilled Time Section */}
          {!unbilledLoading && unbilledJobs.length > 0 && (
            <div style={{
              background: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '12px',
              marginBottom: '24px',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setShowUnbilled(!showUnbilled)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#92400e'
                }}
              >
                {showUnbilled ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                <FiClock size={16} />
                Unbilled Time
                <span style={{
                  backgroundColor: '#f59e0b',
                  color: '#fff',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  {unbilledTotal} {unbilledTotal === 1 ? 'job' : 'jobs'}
                </span>
              </button>
              {showUnbilled && (
                <div style={{ padding: '0 16px 16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #fde68a' }}>
                        <th style={{ textAlign: 'left', padding: '8px 8px', color: '#92400e', fontWeight: '600' }}>Job</th>
                        <th style={{ textAlign: 'left', padding: '8px 8px', color: '#92400e', fontWeight: '600' }}>Client</th>
                        <th style={{ textAlign: 'right', padding: '8px 8px', color: '#92400e', fontWeight: '600' }}>Hours</th>
                        <th style={{ textAlign: 'right', padding: '8px 8px', color: '#92400e', fontWeight: '600' }}>Entries</th>
                        <th style={{ padding: '8px 8px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(showAllUnbilled ? unbilledJobs : unbilledJobs.slice(0, 10)).map(job => (
                        <tr key={job.job_id} style={{ borderBottom: '1px solid #fef3c7' }}>
                          <td style={{ padding: '8px 8px', fontWeight: '500' }}>{job.job_number}</td>
                          <td style={{ padding: '8px 8px', color: '#6b7280' }}>{job.client_name}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right' }}>{job.total_hours}h</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: '#6b7280' }}>{job.entry_count}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={async () => {
                                try {
                                  const config = await getAuthConfig();
                                  await axios.post(`${API_URL}/jobs/${job.job_id}/mark-billed`, {}, config);
                                  loadUnbilledTime();
                                } catch (err) {
                                  alert('Failed to mark as billed');
                                }
                              }}
                              style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: '#10b981',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              Mark Billed
                            </button>
                            <button
                              onClick={() => navigate(`/billing/invoices/new?job_id=${job.job_id}`)}
                              style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: '#f59e0b',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              Create Invoice
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {unbilledJobs.length > 10 && (
                    <div style={{ textAlign: 'center', paddingTop: '10px' }}>
                      <button
                        onClick={() => setShowAllUnbilled(!showAllUnbilled)}
                        style={{
                          background: 'none',
                          border: '1px solid #f59e0b',
                          borderRadius: '6px',
                          padding: '6px 16px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#92400e',
                          cursor: 'pointer'
                        }}
                      >
                        {showAllUnbilled ? 'Show Less' : `Show All ${unbilledJobs.length} Jobs`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Invoice Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  style={{
                    padding: '10px 12px 10px 38px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    width: '250px'
                  }}
                />
              </div>
              <select
                value={invoiceStatusFilter}
                onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Status</option>
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Paid">Paid</option>
                <option value="Overdue">Overdue</option>
                <option value="Imported">Imported (Info-Retriever)</option>
                <option value="Imported-unpaid">Imported – unpaid</option>
              </select>
            </div>
            <button
              onClick={() => navigate('/billing/invoices/new')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <FiPlus /> New Invoice
            </button>
          </div>

          {/* Invoice Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflowX: 'auto'
          }}>
            {invoiceLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading invoices...</div>
            ) : filteredInvoices.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                No invoices found. Create your first invoice!
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151', width: '80px' }}>Inv #</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151', maxWidth: '180px' }}>Client</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151', width: '100px' }}>Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151', width: '90px' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#374151', width: '110px' }}>Amount</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#374151', width: '110px' }}>Balance</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#374151', width: '130px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => {
                    const statusStyle = getStatusStyle(invoice.status);
                    return (
                      <tr
                        key={invoice.id}
                        style={{ borderBottom: '1px solid #e5e7eb' }}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: '600', color: '#2563eb', fontSize: '14px' }}>
                          {invoice.invoice_number}
                        </td>
                        <td style={{ padding: '12px 16px', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>
                          {invoice.bill_to_company ? (
                            <>
                              <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '14px' }}>{invoice.bill_to_company}</div>
                              {invoice.bill_to_name && (
                                <div style={{ fontSize: '13px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{invoice.bill_to_name}</div>
                              )}
                            </>
                          ) : (
                            <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '14px' }}>{invoice.bill_to_name || invoice.client_name}</div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>{formatDate(invoice.invoice_date)}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '9999px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {invoice.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>
                          {formatCurrency(invoice.total)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: (invoice.balance_due || 0) > 0.005 ? '#dc2626' : '#9ca3af' }}>
                          {formatCurrency(invoice.balance_due)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button
                              onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                              style={{ padding: '6px', background: '#eff6ff', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#2563eb' }}
                              title="View"
                            >
                              <FiEye size={16} />
                            </button>
                            <button
                              onClick={() => navigate(`/billing/invoices/${invoice.id}/edit`)}
                              style={{ padding: '6px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
                              title="Edit"
                            >
                              <FiEdit size={16} />
                            </button>
                            {invoice.status === 'Draft' && (
                              <button
                                onClick={() => handleSendInvoice(invoice)}
                                style={{ padding: '6px', background: '#ecfdf5', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#059669' }}
                                title="Send"
                              >
                                <FiSend size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => { setItemToDelete(invoice); setDeleteType('invoice'); setShowDeleteModal(true); }}
                              style={{ padding: '6px', background: '#fef2f2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}
                              title="Delete"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Load More Button */}
            {hasMoreInvoices && filteredInvoices.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px'
              }}>
                <button
                  onClick={() => loadInvoices(false, true)}
                  disabled={invoiceLoading}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                    opacity: invoiceLoading ? 0.7 : 1
                  }}
                >
                  {invoiceLoading ? 'Loading...' : `Load More (${totalInvoices - filteredInvoices.length} remaining)`}
                </button>
              </div>
            )}

            {/* Invoice count */}
            {filteredInvoices.length > 0 && (
              <div style={{ padding: '8px 16px', color: '#666', fontSize: '13px', textAlign: 'center' }}>
                Showing {filteredInvoices.length} of {totalInvoices} invoices
              </div>
            )}
          </div>
        </>
      )}

      {/* Quotes Tab Content */}
      {activeTab === 'quotes' && (
        <>
          {/* Quote Stats */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Total Value</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>{formatCurrency(quoteStats.total)}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Draft</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#374151' }}>{quoteStats.draft}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Sent</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1d4ed8' }}>{quoteStats.sent}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Approved</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#166534' }}>{quoteStats.approved}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>Declined</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{quoteStats.declined}</div>
            </div>
          </div>

          {/* Quote Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search quotes..."
                  value={quoteSearch}
                  onChange={(e) => setQuoteSearch(e.target.value)}
                  style={{
                    padding: '10px 12px 10px 38px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    width: '250px'
                  }}
                />
              </div>
              <select
                value={quoteStatusFilter}
                onChange={(e) => setQuoteStatusFilter(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Status</option>
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Approved">Approved</option>
                <option value="Declined">Declined</option>
                <option value="Expired">Expired</option>
                <option value="Converted">Converted</option>
              </select>
            </div>
            <button
              onClick={() => navigate('/billing/quotes/new')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                backgroundColor: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <FiPlus /> New Quote
            </button>
          </div>

          {/* Quote Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {quoteLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading quotes...</div>
            ) : filteredQuotes.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                No quotes found. Create your first quote!
              </div>
            ) : isMobile ? (
              /* Mobile: card list */
              <div style={{ padding: '8px' }}>
                {filteredQuotes.map((quote) => {
                  const statusStyle = getStatusStyle(quote.status);
                  return (
                    <div
                      key={quote.id}
                      onClick={() => navigate(`/billing/quotes/${quote.id}`)}
                      style={{
                        padding: '14px',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                        <div>
                          <span style={{ fontWeight: '600', color: '#16a34a', fontSize: '15px' }}>{quote.quote_number}</span>
                          <span style={{ margin: '0 8px', color: '#d1d5db' }}>|</span>
                          <span style={{ fontWeight: '500', fontSize: '14px' }}>
                            {quote.bill_to_company || quote.bill_to_name || quote.client_name}
                          </span>
                        </div>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                          whiteSpace: 'nowrap'
                        }}>
                          {quote.status}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{formatDate(quote.quote_date)}</span>
                        <span style={{ fontWeight: '600', fontSize: '15px' }}>{formatCurrency(quote.total)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Desktop: table */
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Quote #</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Client</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Date</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Valid Until</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Status</th>
                    <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Total</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((quote) => {
                    const statusStyle = getStatusStyle(quote.status);
                    return (
                      <tr key={quote.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '14px 16px', fontWeight: '600', color: '#16a34a' }}>
                          {quote.quote_number}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          {quote.bill_to_company ? (
                            <>
                              <div style={{ fontWeight: '500' }}>{quote.bill_to_company}</div>
                              {quote.bill_to_name && (
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>{quote.bill_to_name}</div>
                              )}
                            </>
                          ) : (
                            <div style={{ fontWeight: '500' }}>{quote.bill_to_name || quote.client_name}</div>
                          )}
                        </td>
                        <td style={{ padding: '14px 16px', color: '#6b7280' }}>{formatDate(quote.quote_date)}</td>
                        <td style={{ padding: '14px 16px', color: '#6b7280' }}>{formatDate(quote.valid_until)}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '9999px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {quote.status}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600' }}>
                          {formatCurrency(quote.total)}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => navigate(`/billing/quotes/${quote.id}`)}
                              style={{ padding: '6px', background: '#ecfdf5', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#16a34a' }}
                              title="View"
                            >
                              <FiEye size={16} />
                            </button>
                            {quote.status !== 'Converted' && (
                              <button
                                onClick={() => navigate(`/billing/quotes/${quote.id}/edit`)}
                                style={{ padding: '6px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
                                title="Edit"
                              >
                                <FiEdit size={16} />
                              </button>
                            )}
                            {quote.status === 'Draft' && (
                              <button
                                onClick={() => handleSendQuote(quote)}
                                style={{ padding: '6px', background: '#dbeafe', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#2563eb' }}
                                title="Send"
                              >
                                <FiSend size={16} />
                              </button>
                            )}
                            {quote.status === 'Sent' && (
                              <>
                                <button
                                  onClick={() => handleApproveQuote(quote)}
                                  style={{ padding: '6px', background: '#dcfce7', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#16a34a' }}
                                  title="Approve"
                                >
                                  <FiCheck size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeclineQuote(quote)}
                                  style={{ padding: '6px', background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}
                                  title="Decline"
                                >
                                  <FiX size={16} />
                                </button>
                              </>
                            )}
                            {quote.status === 'Approved' && !quote.converted_to_invoice_id && (
                              <button
                                onClick={() => handleConvertQuote(quote)}
                                style={{ padding: '6px', background: '#ede9fe', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#7c3aed' }}
                                title="Convert to Invoice"
                              >
                                <FiArrowRight size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => { setItemToDelete(quote); setDeleteType('quote'); setShowDeleteModal(true); }}
                              style={{ padding: '6px', background: '#fef2f2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}
                              title="Delete"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Quote Requests Tab Content */}
      {activeTab === 'requests' && (
        <div>
          {/* Status filter pills + import button */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { value: 'new', label: 'New', color: '#ea580c' },
              { value: 'reviewed', label: 'Reviewed', color: '#2563eb' },
              { value: 'converted', label: 'Converted', color: '#16a34a' },
              { value: 'spam', label: 'Spam', color: '#6b7280' },
              { value: 'all', label: 'All', color: '#374151' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setQuoteRequestStatusFilter(opt.value)}
                style={{
                  padding: '6px 14px', fontSize: '13px', fontWeight: '600',
                  borderRadius: '999px', cursor: 'pointer',
                  border: `1.5px solid ${quoteRequestStatusFilter === opt.value ? opt.color : '#e5e7eb'}`,
                  backgroundColor: quoteRequestStatusFilter === opt.value ? `${opt.color}15` : 'white',
                  color: quoteRequestStatusFilter === opt.value ? opt.color : '#6b7280',
                }}
              >
                {opt.label}
              </button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  const phoneRaw = window.prompt("Client's mobile number:");
                  if (!phoneRaw) return;
                  const phone = phoneRaw.replace(/[^0-9+]/g, '');
                  if (!phone) { alert('No phone number entered.'); return; }
                  const link = `${window.location.origin}/get-a-quote`;
                  const body = encodeURIComponent(
                    `Hi! Please fill out a quick form so we can put together a quote for you: ${link}\n\nThanks!\nNorth Georgia Surveying`
                  );
                  window.location.href = `sms:${phone}?body=${body}`;
                }}
                title="Open your phone's text app with the quote request link pre-filled"
                style={{
                  padding: '6px 14px', fontSize: '13px', fontWeight: '600',
                  borderRadius: '8px', cursor: 'pointer',
                  border: '1px solid #fcd34d', backgroundColor: '#fef3c7', color: '#92400e',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FiPhone size={14} /> Text Quote Link
              </button>
              <button
                onClick={() => {
                  const link = `${window.location.origin}/get-a-quote`;
                  navigator.clipboard.writeText(link).then(
                    () => alert(`Quote request link copied:\n\n${link}`),
                    () => window.prompt('Copy this quote request link:', link)
                  );
                }}
                title="Copy the quote request link to paste anywhere"
                style={{
                  padding: '6px 14px', fontSize: '13px', fontWeight: '600',
                  borderRadius: '8px', cursor: 'pointer',
                  border: '1px solid #fde047', backgroundColor: '#fef9c3', color: '#854d0e',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FiInbox size={14} /> Copy Quote Link
              </button>
              <button
                onClick={handleImportFromEmail}
                disabled={importingEmail}
                title="Pull new emails from quotes@northgeorgiasurveying.com (also runs automatically every 15 min)"
                style={{
                  padding: '6px 14px', fontSize: '13px', fontWeight: '600',
                  borderRadius: '8px', cursor: importingEmail ? 'wait' : 'pointer',
                  border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FiMail size={14} /> {importingEmail ? 'Checking…' : 'Refresh from Email'}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '380px 1fr', gap: '16px' }}>
            {/* Left: list */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              {quoteRequestsLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
              ) : quoteRequests.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                  <FiInbox size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <div style={{ fontSize: '14px' }}>No {quoteRequestStatusFilter !== 'all' ? quoteRequestStatusFilter : ''} quote requests.</div>
                  <div style={{ fontSize: '12px', marginTop: '6px' }}>
                    Submissions from the website contact form will land here.
                  </div>
                </div>
              ) : (
                quoteRequests.map(req => {
                  const isSelected = selectedRequestId === req.id;
                  const statusColor = {
                    new: '#ea580c', reviewed: '#2563eb', converted: '#16a34a', spam: '#6b7280',
                  }[req.status] || '#6b7280';
                  return (
                    <div
                      key={req.id}
                      onClick={() => setSelectedRequestId(req.id)}
                      style={{
                        padding: '14px 16px', borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#fff7ed' : 'white',
                        borderLeft: isSelected ? '3px solid #ea580c' : '3px solid transparent',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                          {req.full_name || '(no name)'}
                        </div>
                        <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: statusColor }}>
                          {req.status}
                        </span>
                      </div>
                      {req.survey_type && (
                        <div style={{ fontSize: '12px', color: '#374151', marginBottom: '2px' }}>{req.survey_type}</div>
                      )}
                      {req.property_address && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {req.property_address}
                        </div>
                      )}
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                        {req.received_at ? new Date(req.received_at + 'Z').toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Right: detail */}
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '20px' }}>
              {(() => {
                const req = quoteRequests.find(r => r.id === selectedRequestId);
                if (!req) {
                  return (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                      Select a quote request to view details.
                    </div>
                  );
                }
                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div>
                        <h2 style={{ margin: '0 0 4px', fontSize: '20px', color: '#111827' }}>{req.full_name || '(no name)'}</h2>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                          Received {req.received_at ? new Date(req.received_at + 'Z').toLocaleString() : ''}
                        </div>
                      </div>
                      {req.converted_quote_number && (
                        <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>
                          → {req.converted_quote_number}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                      {req.email && (
                        <div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '600' }}>Email</div>
                          <a href={`mailto:${req.email}`} style={{ fontSize: '14px', color: '#2563eb', textDecoration: 'none', wordBreak: 'break-all' }}>
                            <FiMail size={12} style={{ marginRight: '4px' }} />{req.email}
                          </a>
                        </div>
                      )}
                      {req.phone && (
                        <div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '600' }}>Phone</div>
                          <a href={`tel:${req.phone}`} style={{ fontSize: '14px', color: '#2563eb', textDecoration: 'none' }}>
                            <FiPhone size={12} style={{ marginRight: '4px' }} />{req.phone}
                          </a>
                        </div>
                      )}
                      {req.survey_type && (
                        <div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '600' }}>Survey type</div>
                          <div style={{ fontSize: '14px', color: '#111827' }}>{req.survey_type}</div>
                        </div>
                      )}
                      {req.property_address && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '600' }}>Property</div>
                          <div style={{ fontSize: '14px', color: '#111827' }}>{req.property_address}</div>
                        </div>
                      )}
                    </div>

                    {req.project_details && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px' }}>
                          Project details
                        </div>
                        <div style={{
                          padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px',
                          fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: '1.5',
                        }}>
                          {req.project_details}
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                      {req.status !== 'converted' && (
                        <button
                          onClick={() => handleConvertRequest(req)}
                          style={{
                            padding: '10px 18px', backgroundColor: '#16a34a', color: 'white',
                            border: 'none', borderRadius: '8px', cursor: 'pointer',
                            fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                          }}
                        >
                          <FiArrowRight size={14} /> Convert to Quote
                        </button>
                      )}
                      {req.status === 'new' && (
                        <button
                          onClick={() => handleMarkRequestStatus(req.id, 'reviewed')}
                          style={{
                            padding: '10px 14px', backgroundColor: '#eff6ff', color: '#2563eb',
                            border: '1px solid #bfdbfe', borderRadius: '8px', cursor: 'pointer',
                            fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                          }}
                        >
                          <FiCheck size={14} /> Mark Reviewed
                        </button>
                      )}
                      {req.status !== 'spam' && (
                        <button
                          onClick={() => handleMarkRequestStatus(req.id, 'spam')}
                          style={{
                            padding: '10px 14px', backgroundColor: '#f3f4f6', color: '#6b7280',
                            border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer',
                            fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                          }}
                        >
                          <FiAlertOctagon size={14} /> Mark Spam
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteRequest(req.id)}
                        style={{
                          padding: '10px 14px', backgroundColor: 'white', color: '#dc2626',
                          border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                          marginLeft: 'auto',
                        }}
                      >
                        <FiTrash2 size={14} /> Delete
                      </button>
                    </div>

                    {/* Notes — call/follow-up log so anyone picking up the request knows what's been tried */}
                    <QuoteRequestNotes requestId={req.id} />
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px', color: '#1f2937' }}>Delete {deleteType === 'invoice' ? 'Invoice' : 'Quote'}?</h3>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              Are you sure you want to delete {deleteType === 'invoice' ? itemToDelete?.invoice_number : itemToDelete?.quote_number}? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowDeleteModal(false); setItemToDelete(null); }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#dc2626',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Billing;